import * as React from 'react'

import { cn } from '@ps/ds/lib/utils'
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  Select as SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@ps/ds/ui/select'

export interface SelectOption {
  label: string
  value: string
  icon?: React.ReactNode
  disabled?: boolean
}

export interface SelectProps extends React.ComponentPropsWithoutRef<typeof SelectRoot> {
  options?: SelectOption[]
  placeholder?: string
  triggerClassName?: string
  contentClassName?: string
  children?: React.ReactNode
  hasError?: boolean
  'aria-required'?: boolean
}

const Select = React.forwardRef<React.ElementRef<typeof SelectTrigger>, SelectProps>(
  (
    {
      options,
      placeholder,
      triggerClassName,
      contentClassName,
      children,
      hasError = false,
      ...props
    },
    ref,
  ) => {
    // If children are provided, use nested composition pattern
    if (children) {
      return <SelectRoot {...props}>{children}</SelectRoot>
    }
    // If options are provided, render them
    if (options && options.length > 0) {
      return (
        <SelectRoot {...props}>
          <SelectTrigger
            ref={ref}
            className={cn('bg-background hover:bg-background', triggerClassName)}
            aria-invalid={hasError}
            aria-required={props['aria-required']}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className={contentClassName}>
            <SelectGroup>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                  <div className="flex items-center gap-2">
                    {option.icon && <span className="flex items-center">{option.icon}</span>}
                    <span className="line-clamp-1 overflow-ellipsis">{option.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </SelectRoot>
      )
    }

    return (
      <SelectRoot {...props}>
        <SelectTrigger
          ref={ref}
          className={cn('bg-background hover:bg-background', triggerClassName)}
          aria-required={props['aria-required']}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className={contentClassName} />
      </SelectRoot>
    )
  },
)

Select.displayName = 'Select'

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
